import { useEffect, useRef } from 'react'
import Spinner from './Spinner'
import './InfiniteScrollTrigger.css'

interface InfiniteScrollTriggerProps {
  hasMore: boolean
  isLoading: boolean
  error?: string | null
  onLoadMore: () => void
  onRetry?: () => void
  label: string
}

// A small, accessible sentinel shared by long project lists. The observer gives pointer users an
// automatic next-page load; the button remains available for keyboard users and environments without
// IntersectionObserver.
export default function InfiniteScrollTrigger({
  hasMore,
  isLoading,
  error = null,
  onLoadMore,
  onRetry = onLoadMore,
  label,
}: InfiniteScrollTriggerProps) {
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore || isLoading || error || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) onLoadMore()
      },
      { rootMargin: '0px 0px 400px 0px' },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [error, hasMore, isLoading, onLoadMore])

  if (!hasMore && !isLoading && !error) return null

  return (
    <div ref={sentinelRef} className="infinite-scroll-trigger" data-testid="infinite-scroll-trigger" aria-live="polite">
      {isLoading ? (
        <span className="infinite-scroll-trigger__status" role="status">
          <Spinner size="sm" label={`Loading more ${label}`} />
          Loading more {label}…
        </span>
      ) : error ? (
        <span className="infinite-scroll-trigger__error" role="alert">
          <span>Couldn’t load more {label}.</span>
          <button type="button" onClick={onRetry} data-testid="infinite-scroll-retry">Retry</button>
        </span>
      ) : (
        <button type="button" className="infinite-scroll-trigger__button" onClick={onLoadMore}>
          Load more {label}
        </button>
      )}
    </div>
  )
}
